import React, { createContext, useContext, useRef } from "react"
import { create, useStore } from "zustand"
import { EpisodeOfCareFragment$data } from "@/modules/patients/detail/episode-of-care/types/episode-of-care-fragment-type.ts"
import { chReferralLetterFragment$key } from "@/modules/patients/detail/referral/ch/services/__generated__/chReferralLetterFragment.graphql"
import { deReferralLetterFragment$key } from "@/modules/patients/detail/referral/de/services/__generated__/deReferralLetterFragment.graphql"
import { TimeToTreatType } from "@/types/__generated__/union-types.ts"

type FragmentRef = deReferralLetterFragment$key | chReferralLetterFragment$key

type State = {
  progressNoteId: string | null
  careProvider: string | null
  referralLetterRef: FragmentRef | null
  timeToTreat: TimeToTreatType | null
  episodeOfCareRef: EpisodeOfCareFragment$data | null
  progressNoteCreatedDate: Date | null
}

type Actions = {
  setProgressNoteId: (progressNoteId: string | null) => void
  setCareProvider: (careProvider: string | null) => void
  setReferralLetterRef: (ref: FragmentRef | null) => void
  setEpisodeOfCareRef: (ref: EpisodeOfCareFragment$data | null) => void
  setTimeToTreat: (value: TimeToTreatType | null) => void
  setProgressNoteCreatedDate: (value: State["progressNoteCreatedDate"]) => void
  reset: () => void
}

const initialState: State = {
  progressNoteId: "",
  careProvider: null,
  referralLetterRef: null,
  timeToTreat: null,
  episodeOfCareRef: null,
  progressNoteCreatedDate: null,
}

const createReferralStore = () => {
  return create<State & Actions>((set) => ({
    ...initialState,
    setProgressNoteId: (progressNoteId) => {
      set({ progressNoteId })
    },
    setCareProvider: (careProvider) => set({ careProvider }),
    setReferralLetterRef: (ref) => set({ referralLetterRef: ref }),
    setEpisodeOfCareRef: (ref) => set({ episodeOfCareRef: ref }),
    setTimeToTreat: (v) => set({ timeToTreat: v }),
    setProgressNoteCreatedDate: (v) => set({ progressNoteCreatedDate: v }),
    reset: () => {
      set(initialState)
    },
  }))
}

type ReferralStoreType = ReturnType<typeof createReferralStore>

const ReferralStoreContext = createContext<ReferralStoreType | null>(null)

type ReferralStoreProviderProps = React.PropsWithChildren

export const ReferralStoreProvider = ({ children }: ReferralStoreProviderProps) => {
  const storeRef = useRef<ReferralStoreType>(undefined)
  if (!storeRef.current) {
    storeRef.current = createReferralStore()
  }
  return <ReferralStoreContext.Provider value={storeRef.current}>{children}</ReferralStoreContext.Provider>
}

export function useReferralStoreContext<T>(selector: (state: State & Actions) => T): T {
  const store = useContext(ReferralStoreContext)
  if (!store) throw new Error("Missing ReferralStoreProvider in the tree")
  return useStore(store, selector)
}
